/* global analytics */

import React, { Component } from 'react';

// Components
import Header from '../components/Header';
import Notification from '../components/Notification';
import Nav from '../components/Nav';
import Loader from '../components/Loader';
import BalancePrompt from '../components/BalancePrompt';
import Overlay from '../components/Overlay';

// Views
import Transactions from './Transactions';
import Welcome from '../views/Welcome';
import Error404 from '../views/Error404';

// Services
import auth from '../services/auth';
import data from '../services/data';
import calc from '../services/calc';
import helper from '../services/helper';

import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import MessageBar from "../components/MessageBar";

export default class App extends Component {

    constructor(props) {
        super(props);
        this.state = {
            isProduction: process.env.NODE_ENV === 'production',
            isInitialized: false,
            isDataLoaded: false,
            isLoading: false,
            daysInFuture: 0,
            balance: 0,
            currentBalance: 0,
            loginAttempts: 0,
            isCellSelected: false,
            lastSelectedCellId: false,
            lastCellBlur: 0,
            isBalancePromptVisible: false,
            data: null,
        };
    }

    componentWillMount() {
        this.data = new data();
        this.auth = new auth((userInfo) => {
            this.setState({
                isInitialized: true,
                user: userInfo
            });
            if(userInfo) this.loadData(userInfo.uid);
        });

        window.addEventListener("keydown", this.handleKeyDown.bind(this), false);

        this.analyticsTrack('App opened');
    }

    loadData(userId) {
        this.setState({ isLoading: true });
        this.data.load(userId, (data) => {
            this.setState({
                isLoading: false,
                isDataLoaded: true,
                data
            }, () => {
                this.recalcBalance();
            });
        });
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown, false);
    }

    onTimeRangeChange(newDaysInFuture) {
        let newCalc = calc.get(newDaysInFuture, this.state);
        this.setState((prevState, props) => {
            return {
                daysInFuture: newDaysInFuture,
                currentBalance: newCalc.balance,
                transactions: newCalc.transactions
            };
        });
        this.analyticsTrack('Time slider changed', {
            value: newDaysInFuture
        });
    }

    recalcBalance() {
        let newCalc = calc.get(this.state.daysInFuture, this.state);
        this.setState({
            currentBalance: newCalc.balance,
            transactions: newCalc.transactions
        });
    }

    handleNavButtonClick(target) {
        switch(target.dataset.action) {
            case 'add':
                this.analyticsTrack('Add button clicked');
                if(helper.isTablePageActive()) {
                    this.analyticsTrack('Transaction added');
                    this.data.addTransaction(this.state.user.uid);
                } else {
                    this.analyticsTrack('Adding transaction failed');
                    this.showNotification("You can't add a transaction on" +
                        " this page.", 'warning');
                }
                break;
            case 'delete':
                this.analyticsTrack('Delete button clicked');
                if(Date.now() - this.state.lastCellBlur < 300) {
                    this.analyticsTrack('Transaction deleted');
                    this.data.deleteTransaction(this.state.user.uid, this.state.lastSelectedCellId);
                } else {
                    this.analyticsTrack('Deleting transaction failed');
                    this.showNotification('Select a cell to delete first.', 'warning');
                }
                break;
            case 'logout':
                this.analyticsTrack('Logout button clicked');
                let confirm = window.confirm("Are you sure you want to log out?");
                if(confirm) {
                    this.analyticsTrack('Logged out');
                    this.auth.logout();
                } else {
                    this.analyticsTrack('Logout cancelled');
                }
                break;
            default:
        }
    }

    showNotification(message, type, duration = 5) {
        this.analyticsTrack('Notification shown', {
            type: type,
            value: message,
        });
        this.setState({
            notification: {
                isShowing: true,
                message,
                type,
                duration,
            }
        });
        setTimeout(() => {
            let notification = this.state.notification;
            notification.isShowing = false;
            this.setState({
                notification
            });
        }, duration * 1000);
    }

    handleKeyDown(event) {
        if(!event.key) return;
        switch(event.key.toLowerCase()) {
            case 'n':
                this.analyticsTrack("'n' key pressed");
                if(document.activeElement.nodeName !== 'INPUT') {
                    event.preventDefault();
                    this.analyticsTrack("Transaction added on 'n' key press");
                    this.data.addTransaction(this.state.user.uid);
                }
                break;
            case 'enter':
                this.analyticsTrack('Enter key pressed');
                if(document.activeElement.nodeName === 'INPUT') {
                    this.analyticsTrack('Cell blurred on enter key');
                    document.activeElement.blur();
                }
                break;
            default:
        }
    }

    onCellInput(cell) {
        switch(cell.action) {
            case 'input':
                this.analyticsTrack('Transaction cell edited');
                this.data.editTransaction(this.state.user.uid, cell.rowId, cell.key, cell.value);
                break;
            case 'warning':
                this.analyticsTrack('Transaction cell invalid value entered');
                this.showNotification(cell.message, 'warning');
                break;
            case 'select':
                this.setState({
                    lastSelectedCellId: cell.rowId,
                    isCellSelected: true,
                });
                break;
            case 'blur':
                this.setState({
                    lastCellBlur: Date.now(),
                    isCellSelected: false,
                });
                break;
            default:
        }
    }

    doLogin(email, password) {
        this.setState({ isLoading: true });
        this.auth.login(email, password, () => {
            this.analyticsTrack('Logged in successfully');
            this.loadData();
        }, (error) => {
            this.analyticsTrack('Logged in failed', {
                value: error
            });
            this.setState((prevState, props) => {
                return {
                    isLoading: false,
                    loginAttempts: ++prevState.loginAttempts
                };
            });
            this.showNotification(error, 'warning');
        });
    }

    doRegister(email, password, name) {
        this.setState({ isLoading: true });
        this.auth.register(email, password, name, () => {
            this.analyticsTrack('Registered', {
                value: email,
                name
            });
            this.loadData();
        }, (error) => {
            this.analyticsTrack('Registration failed', {
                value: error
            });
            this.setState((prevState, props) => {
                return {
                    isLoading: false,
                    loginAttempts: ++prevState.loginAttempts
                };
            });
            this.showNotification(error, 'warning');
        });
    }

    handleBalancePrompt(actionObj) {
        switch(actionObj.action) {
            case 'update':
                this.analyticsTrack('Balance updated', {
                    value: actionObj.value
                });
                this.data.updateBalance(this.state.user.uid, actionObj.value);
                this.setState({
                    isBalancePromptVisible: false
                });
                break;
            case 'invalid':
                this.analyticsTrack('Balance invalid value entered');
                this.showNotification('Specify a valid amount.', 'warning');
                break;
            default:
        }
    }

    analyticsTrack(key, valueObj) {
        if(this.state.isProduction) {
            // In case you want to use analytics, you can add your code here.
            if(valueObj) {
              analytics.track(key, valueObj);
            } else {
              analytics.track(key);
            }
        }
    }

    onBalanceClick() {
        this.analyticsTrack('Balance popover opened');
        this.setState({
            isBalancePromptVisible: true
        });
    }

    handleHelpClose() {
        this.analyticsTrack('Welcome screen button clicked');
        this.closePopover();
    }

    handleTwitterClick() {
        this.analyticsTrack('Twitter handle clicked', {
            value: '@Jeroenrs'
        });
    }

    handleOverlayClick() {
        this.analyticsTrack('Overlay clicked');
        this.closePopover();
    }

    closePopover() {
        this.setState({
            isBalancePromptVisible: false,
        });
    }

    render() {
        let style = {
            whenLoggedIn: {
                display: this.state.user ? 'block' : 'none'
            },
            whenDataInit: {
                display: this.state.isInitialized ? 'block' : 'none'
            }
        };

        return (
            <BrowserRouter>
                <div className="App">
                    <div className="desktop">
                        <Loader isVisible={this.state.isLoading || !this.state.isInitialized} />
                        <div style={style.whenDataInit}>
                            <Notification data={this.state.notification} />

                            <div style={style.whenLoggedIn}>
                                <BalancePrompt isVisible={this.state.isBalancePromptVisible} onAction={this.handleBalancePrompt.bind(this)} />
                                <Overlay isVisible={this.state.isBalancePromptVisible} onClick={this.handleOverlayClick.bind(this)} />
                                <MessageBar transactions={this.state.transactions} />
                                <Header
                                    daysInFuture={this.state.daysInFuture}
                                    balance={this.state.currentBalance}
                                    onBalanceClick={this.onBalanceClick.bind(this)}
                                    onTimeRangeChange={this.onTimeRangeChange.bind(this)} />
                            </div>

                            <Switch>
                                {this.state.user ? null : <Route path='/' render={props => <Welcome
                                    onLogin={this.doLogin.bind(this)}
                                    onRegister={this.doRegister.bind(this)}
                                    loginAttempts={this.state.loginAttempts}
                                    {...props}
                                />} /> }
                                <Redirect exact from="/" to="/income/single" />
                                <Route path='/income/single'
                                       render={props => <Transactions onCellInput={this.onCellInput.bind(this)} data={this.state.data ? this.state.data.incomeSingle : null} {...props} />} />
                                <Route path='/income/month'
                                       render={props => <Transactions onCellInput={this.onCellInput.bind(this)} data={this.state.data ? this.state.data.incomeMonth : null} {...props} />} />
                                <Route path='/expenses/single'
                                       render={props => <Transactions onCellInput={this.onCellInput.bind(this)} data={this.state.data ? this.state.data.expensesSingle : null} {...props} />} />
                                <Route path='/expenses/month'
                                       render={props => <Transactions onCellInput={this.onCellInput.bind(this)} data={this.state.data ? this.state.data.expensesMonth : null} {...props} />} />
                                <Route component={Error404} />
                            </Switch>

                            <div style={style.whenLoggedIn}>
                                <Nav isCellSelected={this.state.isCellSelected} onClick={this.handleNavButtonClick.bind(this)} />
                            </div>
                        </div>
                    </div>
                    <div className="mobile"><h1 style={style.h1}>Moneylist</h1><p>Thank you for trying out Moneylist. The app is in early beta and does not have a mobile version at this point. Please open the app on your laptop. <span role="img" aria-label="emojicomputer">👨‍💻</span></p></div>
                </div>
            </BrowserRouter>
        );
    }
}



// WEBPACK FOOTER //
// ./src/views/App.js